import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ErrorModal from './ErrorModal';

const OrganisationModal = ({ isOpen, onClose, organisation, onSuccess }) => {
  const isEditMode = !!organisation;
  
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  
  // Store original admin email for change detection in edit mode
  const [originalAdminEmail, setOriginalAdminEmail] = useState('');
  
  const [formData, setFormData] = useState({
    // Organisation fields
    name: '',
    street_address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    organisation_type: 'Limited Company',
    business_category: 'General Construction',
    email: '',
    phone: '',
    website: '',
    company_number: '',
    logo: '',
    status: true,
    subscriptionTier: 'Pro',
    displayTier: 'Starter', // For display only, not submitted
    flash_upgrade: false,
    registrationDate: new Date().toISOString().split('T')[0],
    // Admin user fields
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_username: '',
    admin_phone_number: '',
    admin_address: '',
    admin_dob: ''
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Email validation states
  const [orgEmailValidation, setOrgEmailValidation] = useState({ valid: true, message: '', checking: false });
  const [adminEmailValidation, setAdminEmailValidation] = useState({ valid: true, message: '', checking: false });
  
  // Email confirmation modal state
  const [emailConfirmModal, setEmailConfirmModal] = useState({
    show: false,
    type: 'create', // 'create' or 'update'
    orgData: null,
    adminData: null,
    changes: [],  // Track changes for update emails
    sending: false
  });

  // Flash upgrade loading state
  const [flashUpgradeLoading, setFlashUpgradeLoading] = useState(false);

  // Admin email change confirmation modal state
  const [adminEmailChangeModal, setAdminEmailChangeModal] = useState({
    show: false,
    oldEmail: '',
    newEmail: '',
    adminName: '',
    onConfirm: null
  });
  
  // Grace period management state
  const [gracePeriodDays, setGracePeriodDays] = useState(14);
  const [refreshingGrace, setRefreshingGrace] = useState(false);
  const [graceRefreshSuccess, setGraceRefreshSuccess] = useState(false);

  // Admin password status and resend state
  const [adminPasswordSet, setAdminPasswordSet] = useState(true);
  const [adminUserId, setAdminUserId] = useState(null);
  const [isResendingAdminPassword, setIsResendingAdminPassword] = useState(false);

  // Fetch subscription packages and business categories
  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionPackages();
      fetchBusinessCategories();
    }
  }, [isOpen]);

  const fetchSubscriptionPackages = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/subscription-packages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSubscriptionPackages(response.data || []);
    } catch (error) {
      console.error('Error fetching subscription packages:', error);
      // Set default packages as fallback
      setSubscriptionPackages([
        { tier_key: 'starter', name: 'Starter' },
        { tier_key: 'business', name: 'Business' },
        { tier_key: 'pro', name: 'Pro' }
      ]);
    }
  };

  // Country options
  const countryOptions = [
    'United Kingdom',
    'Ireland',
    'United States',
    'Canada',
    'Australia',
    'New Zealand',
    'Other'
  ];

  // Organisation type options
  const organisationTypeOptions = [
    'Sole Trader',
    'Limited Company',
    'Other'
  ];

  // Fetch business categories from backend
  const fetchBusinessCategories = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/business-categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const categories = response.data || [];
      setBusinessCategories(categories.map(cat => cat.name));
    } catch (error) {
      console.error('Error fetching business categories:', error);
      // Set default categories as fallback
      setBusinessCategories([
        'General Construction',
        'House Building',
        'Commercial Building',
        'Civil Engineering',
        'Groundworks',
        'Brickwork & Masonry',
        'Roofing',
        'Carpentry & Joinery',
        'Electrical Contracting',
        'Plumbing & Heating',
        'Plastering & Rendering',
        'Painting & Decorating',
        'Scaffolding',
        'Demolition',
        'Restoration & Refurbishment',
        'Fit-Out',
        'Property Maintenance',
        'Specialist Trade'
      ]);
    }
  };

  // Load existing data when editing
  useEffect(() => {
    if (organisation && isEditMode) {
      // Normalize country value
      let countryValue = organisation.country || 'United Kingdom';
      if (countryValue === 'UK' || countryValue === 'United Kingdom') {
        countryValue = 'United Kingdom';
      }

      setFormData(prev => ({
        ...prev,
        name: organisation.name || '',
        street_address: organisation.street_address || '',
        city: organisation.city || '',
        postcode: organisation.postcode || '',
        country: countryValue,
        organisation_type: organisation.organisation_type || 'Limited Company',
        business_category: organisation.business_category || 'General Construction',
        email: organisation.email || '',
        phone: organisation.phone || '',
        website: organisation.website || '',
        company_number: organisation.company_number || '',
        logo: organisation.logo || '',
        status: organisation.status === 'Active',
        // IMPORTANT: Store the ACTUAL subscriptionTier (PayPal truth), not effective_tier
        // effective_tier is only for display purposes
        subscriptionTier: organisation.subscriptionTier || 'Starter',
        // Store effective tier separately for display only (not submitted to backend)
        displayTier: organisation.effective_tier || organisation.subscriptionTier || 'Starter',
        flash_upgrade: organisation.flash_upgrade || false,
        registrationDate: organisation.registrationDate || new Date().toISOString().split('T')[0]
      }));

      fetchAdminUser(organisation.id);
    } else {
      setFormData({
        name: '',
        street_address: '',
        city: '',
        postcode: '',
        country: 'United Kingdom',
        organisation_type: 'Limited Company',
        business_category: 'General Construction',
        email: '',
        phone: '',
        website: '',
        company_number: '',
        logo: '',
        status: true,
        subscriptionTier: 'Pro',
        displayTier: 'Starter',
        flash_upgrade: false,
        registrationDate: new Date().toISOString().split('T')[0],
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_username: '',
        admin_phone_number: '',
        admin_address: '',
        admin_dob: ''
      });
    }
    setErrors({});
    setOrgEmailValidation({ valid: true, message: '', checking: false });
    setAdminEmailValidation({ valid: true, message: '', checking: false });
    // Reset logo upload state
    setLogoFile(null);
    setLogoPreview(null);
    setUploadingLogo(false);
  }, [organisation, isOpen, isEditMode]);

  const fetchAdminUser = async (orgId) => {
    try {
      const response = await axios.get(`${API}/super-admin/organisation-admin/${orgId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.admin_user) {
        const admin = response.data.admin_user;
        setFormData(prev => ({
          ...prev,
          admin_first_name: admin.first_name || '',
          admin_last_name: admin.last_name || '',
          admin_email: admin.email || '',
          admin_username: admin.username || '',
          admin_phone_number: admin.phone_number || '',
          admin_address: admin.address || '',
          admin_dob: admin.date_of_birth || ''
        }));
        // Store original email for change detection
        setOriginalAdminEmail(admin.email || '');
        // Store admin password status and ID
        setAdminPasswordSet(admin.password_set !== false);
        setAdminUserId(admin.id || null);
      }
    } catch (error) {
      console.error('Error fetching admin user:', error);
    }
  };

  // Email format validation
  const isValidEmailFormat = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Dynamic email validation on blur
  const handleEmailBlur = async (emailType, email, currentOrgEmail = null) => {
    if (!email || !isValidEmailFormat(email)) return;
    
    // Skip validation if editing and email hasn't changed
    if (isEditMode && emailType === 'organisation' && email === organisation?.email) {
      setOrgEmailValidation({ valid: true, message: '', checking: false });
      return;
    }
    
    // Skip validation if editing admin email and it hasn't changed
    if (isEditMode && emailType === 'admin' && email.toLowerCase() === originalAdminEmail.toLowerCase()) {
      setAdminEmailValidation({ valid: true, message: '', checking: false });
      return;
    }
    
    const setValidation = emailType === 'organisation' ? setOrgEmailValidation : setAdminEmailValidation;
    setValidation({ valid: true, message: '', checking: true });
    
    try {
      const response = await axios.post(`${API}/public/validate-email`, {
        email: email,
        type: emailType === 'organisation' ? 'organisation' : 'admin'
      });
      
      if (response.data.available) {
        setValidation({ valid: true, message: response.data.message, checking: false });
      } else {
        setValidation({ valid: false, message: response.data.message, checking: false });
      }
    } catch (err) {
      console.error('Email validation error:', err);
      setValidation({ valid: true, message: '', checking: false });
    }
  };

  // Handle logo file selection
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
        return;
      }
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'File size must be less than 2MB' }));
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, logo: null }));
    }
  };

  // Upload logo to server
  const uploadLogo = async (orgId) => {
    if (!logoFile) return null;
    
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);
      
      const response = await axios.post(
        `${API}/super-admin/organisations/${orgId}/logo`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data.logo_url;
    } catch (err) {
      console.error('Logo upload failed:', err);
      throw err;
    } finally {
      setUploadingLogo(false);
    }
  };

  // Clear logo
  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo: '' }));
  };

  // Generate username from first and last name
  const generateUsername = (firstName, lastName) => {
    if (!firstName || !lastName) return '';
    const firstInitial = firstName.charAt(0).toLowerCase();
    const lastNameClean = lastName.toLowerCase().trim().replace(/\s+/g, '');
    return `${firstInitial}.${lastNameClean}`;
  };

  const validateForm = async () => {
    const newErrors = {};
    
    // Organisation fields
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.street_address.trim()) newErrors.street_address = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Organisation email is required';
    } else if (!isValidEmailFormat(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (!orgEmailValidation.valid) {
      newErrors.email = orgEmailValidation.message;
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    
    // Admin user fields
    if (!formData.admin_first_name.trim()) newErrors.admin_first_name = 'First name is required';
    if (!formData.admin_last_name.trim()) newErrors.admin_last_name = 'Last name is required';
    
    // Admin email validation
    if (!formData.admin_email.trim()) {
      newErrors.admin_email = 'Admin email is required';
    } else if (!isValidEmailFormat(formData.admin_email)) {
      newErrors.admin_email = 'Invalid email format';
    } else if (!adminEmailValidation.valid) {
      newErrors.admin_email = adminEmailValidation.message;
    }
    
    // Username is auto-generated for new orgs, read-only for existing
    // No validation needed as it will be generated on submit
    
    if (!formData.admin_phone_number.trim()) newErrors.admin_phone_number = 'Phone number is required';
    if (!formData.admin_address.trim()) newErrors.admin_address = 'Address is required';
    if (!formData.admin_dob) newErrors.admin_dob = 'Date of birth is required';
    
    // No password validation - password will be set via email link
    
    // Additional server-side validation for emails if not already checked
    if (!newErrors.email && formData.email && (!isEditMode || formData.email !== organisation?.email)) {
      try {
        const response = await axios.post(`${API}/public/validate-email`, {
          email: formData.email,
          type: 'organisation'
        });
        if (!response.data.available) {
          newErrors.email = response.data.message;
        }
      } catch (err) {
        console.error('Email validation error:', err);
      }
    }
    
    if (!newErrors.admin_email && formData.admin_email && !isEditMode) {
      try {
        const response = await axios.post(`${API}/public/validate-email`, {
          email: formData.admin_email,
          type: 'admin'
        });
        if (!response.data.available) {
          newErrors.admin_email = response.data.message;
        }
      } catch (err) {
        console.error('Admin email validation error:', err);
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleToggleStatus = () => {
    setFormData(prev => ({ ...prev, status: !prev.status }));
  };

  // Helper function to detect if organisation is ACTUALLY on Pro tier (via PayPal subscription)
  // This should NOT check effective_tier because that includes flash upgrades
  // We need to know the real subscription tier to determine if flash upgrade is applicable
  const isOrganisationProTier = (org) => {
    if (!org) return false;
    
    // Only check the actual stored/PayPal tier, NOT effective_tier
    // effective_tier includes flash upgrades which would incorrectly block the toggle
    const tierName = (org.subscriptionTier || '').toLowerCase().trim();
    const tierPackage = (org.subscription_package_id || '').toLowerCase().trim();
    
    if (tierName === 'pro') return true;
    if (tierPackage === 'pro') return true;
    if (tierPackage.startsWith('pro-') || tierPackage.startsWith('pro_')) return true;
    
    return false;
  };

  const handleToggleFlashUpgrade = async () => {
    // Only allow toggle in edit mode
    if (!isEditMode || !organisation) return;
    
    // Prevent rapid clicking
    if (flashUpgradeLoading) return;
    
    const isCurrentlyEnabled = formData.flash_upgrade;
    const isProTier = isOrganisationProTier(organisation);
    
    // Check if org is on Pro tier - flash upgrade not applicable
    if (isProTier && !isCurrentlyEnabled) {
      setErrorModal({
        show: true,
        message: 'This organisation is already on the Pro tier. Flash upgrade is only available for organisations on lower tiers.'
      });
      return;
    }
    
    setFlashUpgradeLoading(true);
    
    try {
      if (isCurrentlyEnabled) {
        // Disable flash upgrade
        await axios.post(`${API}/super-admin/flash-upgrades/disable`, {
          organisation_id: organisation.organisation_id
        });
      } else {
        // Enable flash upgrade
        await axios.post(`${API}/super-admin/flash-upgrades/enable`, {
          organisation_id: organisation.organisation_id
        });
      }
      
      // Update local state
      setFormData(prev => ({ ...prev, flash_upgrade: !isCurrentlyEnabled }));
      
    } catch (error) {
      console.error('Error toggling flash upgrade:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update flash upgrade';
      setErrorModal({
        show: true,
        message: errorMessage
      });
    } finally {
      setFlashUpgradeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Check if admin email has changed in edit mode
    const adminEmailHasChanged = isEditMode && 
      originalAdminEmail && 
      formData.admin_email.toLowerCase() !== originalAdminEmail.toLowerCase();

    if (adminEmailHasChanged) {
      // Show email change confirmation modal
      setAdminEmailChangeModal({
        show: true,
        oldEmail: originalAdminEmail,
        newEmail: formData.admin_email,
        adminName: `${formData.admin_first_name} ${formData.admin_last_name}`.trim(),
        onConfirm: () => performSubmit(true)
      });
      return;
    }

    // No email change, proceed directly
    await performSubmit(false);
  };

  // Actual submit function (called directly or after email change confirmation)
  const performSubmit = async (adminEmailChanged) => {
    // Close the email change modal if it was open
    setAdminEmailChangeModal({ show: false, oldEmail: '', newEmail: '', adminName: '', onConfirm: null });

    setSaving(true);
    try {
      // For new organisations, username will be auto-generated by backend
      // For existing organisations, use the existing username
      const usernameToSubmit = isEditMode ? formData.admin_username : undefined;
      
      const submitData = {
        name: formData.name,
        street_address: formData.street_address,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country,
        organisation_type: formData.organisation_type,
        business_category: formData.business_category,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        company_number: formData.company_number,
        logo: formData.logo,
        status: formData.status ? 'Active' : 'Inactive',
        subscriptionTier: formData.subscriptionTier,
        flash_upgrade: formData.flash_upgrade,
        registrationDate: formData.registrationDate,
        admin_user: {
          first_name: formData.admin_first_name,
          last_name: formData.admin_last_name,
          email: formData.admin_email,
          username: usernameToSubmit,
          phone_number: formData.admin_phone_number,
          address: formData.admin_address,
          date_of_birth: formData.admin_dob
        }
      };

      let responseData = null;
      let generatedUsername = formData.admin_username;

      if (isEditMode) {
        const response = await axios.put(
          `${API}/super-admin/managed-organisations-with-admin/${organisation.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        responseData = response.data;
        
        // Check if enforcement was applied
        if (response.data?.enforcement?.enforcement_applied) {
          const enforcement = response.data.enforcement;
          let enforcementMsg = 'Organisation updated. Due to subscription downgrade:\n';
          if (enforcement.sites_deactivated > 0) {
            enforcementMsg += `\n- ${enforcement.sites_deactivated} site(s) deactivated (new limit: ${enforcement.new_limits.max_sites})`;
          }
          if (enforcement.members_deactivated > 0) {
            enforcementMsg += `\n- ${enforcement.members_deactivated} team member(s) deactivated (new limit: ${enforcement.new_limits.max_members})`;
          }
          enforcementMsg += '\n\nThe admin will need to manually reactivate sites and members within the new limits.';
          
          alert(enforcementMsg);
        }

        // Get the changes from the response
        const changes = response.data?.changes || [];

        // Check if admin email was changed and show appropriate message
        if (response.data?.email_change) {
          const emailChangeInfo = response.data.email_change;
          // Show special email confirmation modal for email changes
          setEmailConfirmModal({
            show: true,
            type: 'email_change',
            orgData: {
              organisation_id: organisation.organisation_id || responseData?.organisation_id,
              company_name: formData.name,
              subscription_tier: formData.subscriptionTier
            },
            adminData: {
              admin_email: formData.admin_email,
              admin_name: `${formData.admin_first_name} ${formData.admin_last_name}`,
              admin_username: formData.admin_username,
              old_email: emailChangeInfo.old_email,
              email_change_message: emailChangeInfo.message
            },
            changes: changes,
            sending: false
          });
        } else {
          // Show regular email confirmation modal for updates
          setEmailConfirmModal({
            show: true,
            type: 'update',
            orgData: {
              organisation_id: organisation.organisation_id || responseData?.organisation_id,
              company_name: formData.name,
              subscription_tier: formData.subscriptionTier
            },
            adminData: {
              admin_email: formData.admin_email,
              admin_name: `${formData.admin_first_name} ${formData.admin_last_name}`,
              admin_username: formData.admin_username
            },
            changes: changes,
            sending: false
          });
        }
      } else {
        const response = await axios.post(
          `${API}/super-admin/managed-organisations-with-admin`,
          submitData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        responseData = response.data;
        generatedUsername = response.data?.generated_username || response.data?.admin_user?.username;
        
        // Upload logo if a file was selected (for new organisations)
        const newOrgId = responseData?.organisation?.id || responseData?.id;
        if (logoFile && newOrgId) {
          try {
            await uploadLogo(newOrgId);
          } catch (logoErr) {
            console.error('Logo upload failed, but organisation was created:', logoErr);
          }
        }
        
        // Show email confirmation modal for new organisations
        setEmailConfirmModal({
          show: true,
          type: 'create',
          orgData: {
            organisation_id: responseData?.organisation?.organisation_id || responseData?.organisation_id,
            company_name: formData.name,
            subscription_tier: formData.subscriptionTier
          },
          adminData: {
            admin_email: formData.admin_email,
            admin_name: `${formData.admin_first_name} ${formData.admin_last_name}`,
            admin_username: generatedUsername
          },
          sending: false
        });
      }
      
      // Upload logo if a file was selected (for edited organisations)
      if (isEditMode && logoFile && organisation?.id) {
        try {
          await uploadLogo(organisation.id);
        } catch (logoErr) {
          console.error('Logo upload failed, but organisation was updated:', logoErr);
        }
      }
      
      // Don't close yet - wait for email confirmation modal decision
    } catch (error) {
      console.error('Error saving organisation:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      setErrorModal({
        show: true,
        message: `Failed to save organisation: ${errorMessage}`
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle sending email from confirmation modal
  const handleSendEmail = async () => {
    setEmailConfirmModal(prev => ({ ...prev, sending: true }));
    
    try {
      const endpoint = emailConfirmModal.type === 'create' 
        ? `${API}/super-admin/send-welcome-email`
        : `${API}/super-admin/send-update-email`;
      
      const payload = {
        organisation_id: emailConfirmModal.orgData.organisation_id,
        admin_email: emailConfirmModal.adminData.admin_email,
        admin_name: emailConfirmModal.adminData.admin_name,
        admin_username: emailConfirmModal.adminData.admin_username,
        company_name: emailConfirmModal.orgData.company_name,
        ...(emailConfirmModal.type === 'create' 
          ? { subscription_tier: emailConfirmModal.orgData.subscription_tier }
          : { changes: emailConfirmModal.changes || [] }  // Pass structured changes
        )
      };
      
      const response = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        alert(`Email sent successfully to ${emailConfirmModal.adminData.admin_email}`);
      } else {
        alert(`Failed to send email: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Error sending email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEmailConfirmModal({ show: false, type: 'create', orgData: null, adminData: null, changes: [], sending: false });
      onSuccess();
      onClose();
    }
  };

  // Handle skipping email
  const handleSkipEmail = () => {
    setEmailConfirmModal({ show: false, type: 'create', orgData: null, adminData: null, changes: [], sending: false });
    onSuccess();
    onClose();
  };

  // Handle resending password setup email to admin
  const handleResendAdminPasswordEmail = async () => {
    if (!adminUserId || !formData.admin_email) return;
    
    setIsResendingAdminPassword(true);
    try {
      const response = await axios.post(
        `${API}/super-admin/admins/${adminUserId}/resend-password-email`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        alert(`Password setup email sent to ${formData.admin_email}`);
      } else {
        alert(`Failed to send email: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending password email:', error);
      alert(`Error: ${error.response?.data?.detail || 'Failed to send password setup email'}`);
    } finally {
      setIsResendingAdminPassword(false);
    }
  };

  // Handle refreshing grace period for an organisation
  const handleRefreshGracePeriod = async () => {
    if (!organisation?.organisation_id) return;
    
    setRefreshingGrace(true);
    setGraceRefreshSuccess(false);
    try {
      const response = await axios.post(
        `${API}/super-admin/organisations/${organisation.organisation_id}/refresh-grace-period`,
        { days: gracePeriodDays },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        setGraceRefreshSuccess(true);
        // Reset success message after 3 seconds
        setTimeout(() => setGraceRefreshSuccess(false), 3000);
        // Refresh the organisation data
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error refreshing grace period:', error);
      setErrorModal({
        show: true,
        message: error.response?.data?.detail || 'Failed to refresh grace period'
      });
    } finally {
      setRefreshingGrace(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {isEditMode ? 'Edit Organisation' : 'Add New Organisation'}
                </h2>
                <p className="text-indigo-100 text-xs">
                  {isEditMode ? 'Update organisation and admin details' : 'Create organisation with admin user'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Organisation Details Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Organisation Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="Enter company name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Organisation ID - Only show in edit mode */}
              {isEditMode && organisation?.organisation_id && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Organisation ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={organisation.organisation_id}
                      disabled
                      className="w-full px-3 py-2 border-2 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200"
                      placeholder="Organisation ID"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">This is a system-generated unique identifier and cannot be changed</p>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Organisation Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={(e) => handleEmailBlur('organisation', e.target.value)}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.email || !orgEmailValidation.valid ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="company@example.com"
                />
                {orgEmailValidation.checking && (
                  <p className="text-gray-500 text-xs mt-1">Checking availability...</p>
                )}
                {!orgEmailValidation.checking && !orgEmailValidation.valid && (
                  <p className="text-red-500 text-xs mt-1">{orgEmailValidation.message}</p>
                )}
                {!orgEmailValidation.checking && orgEmailValidation.valid && orgEmailValidation.message && (
                  <p className="text-green-600 text-xs mt-1">✓ {orgEmailValidation.message}</p>
                )}
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="+44 20 1234 5678"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Street Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="street_address"
                  value={formData.street_address}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.street_address ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="123 Business Street"
                />
                {errors.street_address && <p className="text-red-500 text-xs mt-1">{errors.street_address}</p>}
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.city ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="London"
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>

              {/* Postcode */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Postcode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.postcode ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="SW1A 1AA"
                />
                {errors.postcode && <p className="text-red-500 text-xs mt-1">{errors.postcode}</p>}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.country ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                >
                  {countryOptions.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              {/* Organisation Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Organisation Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="organisation_type"
                  value={formData.organisation_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  {organisationTypeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Business Category */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Business Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="business_category"
                  value={formData.business_category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  {[...businessCategories].sort((a, b) => a.localeCompare(b)).map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="https://www.company.com"
                />
              </div>

              {/* Company Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Company Number</label>
                <input
                  type="text"
                  name="company_number"
                  value={formData.company_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="12345678"
                />
              </div>

              {/* Logo Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Organisation Logo</label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    {(logoPreview || formData.logo) ? (
                      <div className="relative">
                        <img 
                          src={logoPreview || formData.logo} 
                          alt="Logo preview" 
                          className="w-20 h-20 object-contain border-2 border-gray-200 rounded-lg bg-white"
                        />
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Input */}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-flex items-center px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      {logoFile ? 'Change Logo' : 'Upload Logo'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB. Recommended: Square image.</p>
                    {errors.logo && <p className="text-red-500 text-xs mt-1">{errors.logo}</p>}
                    {uploadingLogo && <p className="text-blue-500 text-xs mt-1">Uploading...</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin User Section */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Admin User Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="admin_first_name"
                  value={formData.admin_first_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.admin_first_name ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="John"
                />
                {errors.admin_first_name && <p className="text-red-500 text-xs mt-1">{errors.admin_first_name}</p>}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="admin_last_name"
                  value={formData.admin_last_name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.admin_last_name ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="Doe"
                />
                {errors.admin_last_name && <p className="text-red-500 text-xs mt-1">{errors.admin_last_name}</p>}
              </div>

              {/* Admin Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Admin Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="admin_email"
                  value={formData.admin_email}
                  onChange={handleChange}
                  onBlur={(e) => handleEmailBlur('admin', e.target.value)}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.admin_email || !adminEmailValidation.valid ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="admin@company.com"
                />
                {adminEmailValidation.checking && (
                  <p className="text-gray-500 text-xs mt-1">Checking availability...</p>
                )}
                {!adminEmailValidation.checking && !adminEmailValidation.valid && (
                  <p className="text-red-500 text-xs mt-1">{adminEmailValidation.message}</p>
                )}
                {!adminEmailValidation.checking && adminEmailValidation.valid && adminEmailValidation.message && !isEditMode && (
                  <p className="text-green-600 text-xs mt-1">✓ {adminEmailValidation.message}</p>
                )}
                {errors.admin_email && <p className="text-red-500 text-xs mt-1">{errors.admin_email}</p>}
                {/* Email Change Warning Banner */}
                {isEditMode && originalAdminEmail && formData.admin_email.toLowerCase() !== originalAdminEmail.toLowerCase() && formData.admin_email.trim() !== '' && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="ml-2">
                        <p className="text-xs font-medium text-amber-800">Admin Email Change Warning</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Changing this email will require the admin to verify their new email address. They will not be able to login until verification is complete.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Username - Read-only, auto-generated */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Username
                  {isEditMode && formData.admin_username && (
                    <span className="text-gray-500 text-xs ml-1">(auto-generated)</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="admin_username"
                    value={isEditMode ? formData.admin_username : (formData.admin_first_name && formData.admin_last_name ? generateUsername(formData.admin_first_name, formData.admin_last_name) : '')}
                    disabled
                    className="w-full px-3 py-2 border-2 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200"
                    placeholder={isEditMode ? '' : 'Will be auto-generated'}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                {!isEditMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.admin_first_name && formData.admin_last_name 
                      ? `Username will be: ${generateUsername(formData.admin_first_name, formData.admin_last_name)}`
                      : 'Username will be auto-generated from first and last name (format: f.lastname)'
                    }
                  </p>
                )}
                {isEditMode && !formData.admin_username && (
                  <p className="text-xs text-amber-600 mt-1">Username not yet generated</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="admin_dob"
                  value={formData.admin_dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.admin_dob ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                />
                {errors.admin_dob && <p className="text-red-500 text-xs mt-1">{errors.admin_dob}</p>}
              </div>

              {/* Password Info / Resend Password Setup */}
              <div className="md:col-span-2">
                {isEditMode && adminPasswordSet === false ? (
                  // Admin has NOT set password - show warning and resend button
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-amber-800">Password Not Set</p>
                          <p className="text-xs text-amber-700 mt-1">
                            This admin has not yet set their password. You can resend the password setup email if needed.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={handleResendAdminPasswordEmail}
                        disabled={isResendingAdminPassword || !formData.admin_email}
                        className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {isResendingAdminPassword ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Resend Password Setup Email
                          </>
                        )}
                      </button>
                      {!formData.admin_email && (
                        <p className="text-xs text-amber-600 mt-2">Cannot send email - no email address on file</p>
                      )}
                    </div>
                  </div>
                ) : (
                  // Admin has set password OR creating new org - show info box
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-800">Password Setup</p>
                        <p className="text-xs text-blue-700 mt-1">
                          {isEditMode 
                            ? "The admin user has set a password for their account. A new password can be set by using the 'forgot password' link on the mobile app or web login screens."
                            : "The admin user will receive an email with a link to set their own password. No password needs to be entered here."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="admin_phone_number"
                  value={formData.admin_phone_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all border-gray-200"
                  placeholder="+44 7700 900000"
                  required
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="admin_address"
                  value={formData.admin_address}
                  onChange={handleChange}
                  rows="2"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all resize-none ${errors.admin_address ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  placeholder="123 Main Street, London, UK, SW1A 1AA"
                />
                {errors.admin_address && <p className="text-red-500 text-xs mt-1">{errors.admin_address}</p>}
              </div>
            </div>
          </div>

          {/* Super Admin Settings Section */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Super Admin Settings
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Status Toggle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${formData.status ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${formData.status ? 'translate-x-9' : 'translate-x-1'}`} />
                  </button>
                  <span className={`text-sm font-semibold ${formData.status ? 'text-green-600' : 'text-gray-500'}`}>
                    {formData.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Flash Upgrade Toggle - Only show in edit mode */}
              {isEditMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Flash Upgrade</label>
                  {(() => {
                    const isProTier = isOrganisationProTier(organisation);
                    
                    if (isProTier) {
                      return (
                        <div className="flex items-center space-x-2">
                          <div className="relative inline-flex h-8 w-16 items-center rounded-full bg-gray-200 cursor-not-allowed opacity-60">
                            <span className="inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform translate-x-1" />
                          </div>
                          <span className="text-sm text-gray-400">N/A</span>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={handleToggleFlashUpgrade}
                          disabled={flashUpgradeLoading}
                          className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                            flashUpgradeLoading ? 'bg-gray-300 cursor-wait' : formData.flash_upgrade ? 'bg-yellow-500' : 'bg-gray-300'
                          } ${flashUpgradeLoading ? 'opacity-60' : ''}`}
                        >
                          {flashUpgradeLoading ? (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </span>
                          ) : (
                            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${formData.flash_upgrade ? 'translate-x-9' : 'translate-x-1'}`} />
                          )}
                        </button>
                        <span className={`text-sm font-semibold ${flashUpgradeLoading ? 'text-gray-400' : formData.flash_upgrade ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {flashUpgradeLoading ? 'Processing...' : formData.flash_upgrade ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    );
                  })()}
                  {(() => {
                    const isProTier = isOrganisationProTier(organisation);
                    if (isProTier) {
                      return <p className="text-xs text-gray-500 mt-1">Not available for Pro tier</p>;
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* Subscription Tier */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Plan</label>
                {isEditMode ? (
                  <div>
                    <div className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed">
                      {/* Show actual tier user has access to (accounting for pending downgrade) */}
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {(() => {
                            // During pending downgrade, show the PREVIOUS tier (what they currently have access to)
                            const displayTier = organisation?.downgrade_pending && organisation?.downgrade_previous_plan_name
                              ? organisation.downgrade_previous_plan_name
                              : formData.subscriptionTier;
                            return subscriptionPackages.find(pkg => 
                              pkg.tier_key.toLowerCase() === (displayTier || '').toLowerCase() ||
                              pkg.name.toLowerCase() === (displayTier || '').toLowerCase()
                            )?.name || displayTier || 'Unknown';
                          })()}
                        </span>
                      </div>
                      {/* State indicators */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {organisation?.flash_upgrade && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                            Flash Upgrade → Pro
                          </span>
                        )}
                        {organisation?.downgrade_pending && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Downgrade → {organisation.downgrade_new_plan_name || 'Lower tier'}
                          </span>
                        )}
                      </div>
                    </div>
                    {organisation?.downgrade_pending && organisation?.downgrade_effective_date && (
                      <p className="text-xs text-amber-600 mt-1">
                        Downgrade effective: {new Date(organisation.downgrade_effective_date).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Subscription tier is managed via PayPal
                    </p>
                  </div>
                ) : (
                  <div>
                    {/* Fixed Pro tier display for create mode */}
                    <div className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed">
                      <span className="font-medium">💎 Pro</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      <span className="font-medium">ℹ️</span> All new organisations start with a 14-day Pro trial, 
                      giving users access to all platform features. They can choose their preferred plan when subscribing.
                    </p>
                  </div>
                )}
              </div>

              {/* Subscription Status (Edit mode only) */}
              {isEditMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Status</label>
                  <div className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-100">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      (() => {
                        switch (organisation?.subscription_status?.toUpperCase()) {
                          case 'ACTIVE': return 'bg-green-100 text-green-800';
                          case 'GRACE_PERIOD': return 'bg-blue-100 text-blue-800';
                          case 'GRACE_EXPIRED': return 'bg-orange-100 text-orange-800';
                          case 'SUSPENDED': return 'bg-red-100 text-red-800';
                          case 'CANCELLED': return 'bg-yellow-100 text-yellow-800';
                          case 'EXPIRED': return 'bg-gray-100 text-gray-800';
                          default: return 'bg-gray-100 text-gray-600';
                        }
                      })()
                    }`}>
                      {organisation?.subscription_status?.replace(/_/g, ' ') || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Status is automatically managed by the subscription system
                  </p>
                </div>
              )}

              {/* Registration Date */}
              <div className="md:col-span-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Registration Date</label>
                <div className="relative">
                  <input
                    type="date"
                    name="registrationDate"
                    value={formData.registrationDate}
                    readOnly
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">📅 {isEditMode ? 'Registration date cannot be changed' : 'Auto-set to today\'s date'}</p>
              </div>
            </div>
          </div>

          {/* Grace Period Management - Only show in edit mode */}
          {isEditMode && (
            <div className="border-t-2 border-gray-100 pt-4 mt-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Grace Period Management
              </h3>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                {/* Current Status */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-gray-600">Current Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    organisation?.subscription_status === 'GRACE_PERIOD' ? 'bg-blue-100 text-blue-800' :
                    organisation?.subscription_status === 'GRACE_EXPIRED' ? 'bg-red-100 text-red-800' :
                    organisation?.subscription_status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                    organisation?.subscription_status === 'SUSPENDED' ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {organisation?.subscription_status || 'Unknown'}
                  </span>
                </div>
                
                {/* Grace period info if applicable */}
                {organisation?.grace_period_start && (
                  <p className="text-xs text-gray-500 mb-3">
                    Grace period started: {new Date(organisation.grace_period_start).toLocaleDateString()}
                    {organisation?.grace_period_days && ` (${organisation.grace_period_days} days)`}
                  </p>
                )}
                
                {/* PayPal subscription info - if they have/had a subscription, disable grace period controls */}
                {organisation?.paypal_subscription_id ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800 font-medium mb-1">
                      PayPal Subscription Linked
                    </p>
                    <p className="text-xs text-blue-600 mb-2">
                      Subscription ID: {organisation.paypal_subscription_id}
                    </p>
                    <p className="text-xs text-gray-600">
                      Grace periods cannot be applied to organisations with a PayPal subscription history. 
                      Subscription access is managed through PayPal.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Refresh Grace Period Controls - Only available before first PayPal subscription */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3 pt-3 border-t border-amber-200">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600 whitespace-nowrap">Days:</label>
                        <input
                          type="number"
                          min="1"
                          max="90"
                          value={gracePeriodDays}
                          onChange={(e) => setGracePeriodDays(parseInt(e.target.value) || 14)}
                          className="w-20 px-2 py-1.5 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      
                      <button
                        type="button"
                        onClick={handleRefreshGracePeriod}
                        disabled={refreshingGrace}
                        className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 flex items-center gap-2 transition-all shadow-sm hover:shadow-md"
                      >
                        {refreshingGrace ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh Grace Period
                          </>
                        )}
                      </button>
                      
                      {/* Success message */}
                      {graceRefreshSuccess && (
                        <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Grace period refreshed!
                        </span>
                      )}
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Refreshing the grace period will reset the trial and allow the organisation to access the platform for the specified number of days.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>{isEditMode ? 'Update' : 'Create'} Organisation</>
              )}
            </button>
          </div>
        </form>
      </div>

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />

      {/* Email Confirmation Modal */}
      {emailConfirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className={`bg-white rounded-xl shadow-2xl mx-4 overflow-hidden ${emailConfirmModal.type !== 'create' && emailConfirmModal.changes?.length > 0 ? 'max-w-2xl w-full' : 'max-w-md w-full'}`}>
            {/* Header */}
            <div className={`px-6 py-4 ${emailConfirmModal.type === 'create' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-cyan-600'} text-white`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {emailConfirmModal.type === 'create' ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold">
                    {emailConfirmModal.type === 'create' ? 'Organisation Created!' : 'Organisation Updated!'}
                  </h3>
                  <p className="text-sm opacity-90">
                    {emailConfirmModal.type === 'create' ? 'Would you like to notify the admin?' : 'Would you like to notify the admin of the changes?'}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Admin:</strong> {emailConfirmModal.adminData?.admin_name}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Email:</strong> {emailConfirmModal.adminData?.admin_email}
                </p>
                {emailConfirmModal.type === 'create' && (
                  <p className="text-sm text-gray-600">
                    <strong>Username:</strong> <code className="bg-gray-200 px-2 py-0.5 rounded">{emailConfirmModal.adminData?.admin_username}</code>
                  </p>
                )}
              </div>

              {/* Show changes table for updates */}
              {emailConfirmModal.type !== 'create' && emailConfirmModal.changes?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Changes Made ({emailConfirmModal.changes.length})
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Field</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Previous</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailConfirmModal.changes.map((change, idx) => (
                          <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${['Status', 'Subscription Plan', 'Flash Upgrade'].includes(change.field) ? 'bg-amber-50' : ''}`}>
                            <td className="px-3 py-2 font-medium text-gray-700">{change.field}</td>
                            <td className="px-3 py-2 text-gray-500 line-through">{change.old_value || '-'}</td>
                            <td className="px-3 py-2 text-green-600 font-medium">{change.new_value || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* No changes message */}
              {emailConfirmModal.type !== 'create' && (!emailConfirmModal.changes || emailConfirmModal.changes.length === 0) && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500 text-center">No changes were detected.</p>
                </div>
              )}

              <p className="text-sm text-gray-600 mb-4">
                {emailConfirmModal.type === 'create' 
                  ? 'Send a welcome email with login credentials to the admin user? The email will include the organisation ID, username, and instructions to log in.'
                  : 'Send an update notification email to the admin user? The email will list all the changes shown above.'
                }
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSkipEmail}
                  disabled={emailConfirmModal.sending}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium disabled:opacity-50"
                >
                  Skip Email
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={emailConfirmModal.sending}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2 ${
                    emailConfirmModal.type === 'create' 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700'
                  }`}
                >
                  {emailConfirmModal.sending ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Email Change Confirmation Modal */}
      {adminEmailChangeModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Admin Email Change</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                You are changing the email address for admin <span className="font-semibold">{adminEmailChangeModal.adminName}</span>:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center text-sm">
                  <span className="text-gray-500">{adminEmailChangeModal.oldEmail}</span>
                  <svg className="w-4 h-4 mx-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="text-indigo-600 font-medium">{adminEmailChangeModal.newEmail}</span>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-2">This will:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Require the admin to verify their new email address
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Prevent admin login until verification is complete
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Send a security notification to the old email address
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setAdminEmailChangeModal({ show: false, oldEmail: '', newEmail: '', adminName: '', onConfirm: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={adminEmailChangeModal.onConfirm}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationModal;
